import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { buildApiUrl } from '../config/api';
import { 
  validateIncome, 
  validateExpense, 
  validateAsset,
  sanitizeForFirestore,
  convertFromFirestore 
} from '../schemas/dataSchemas';

export const useUserData = () => {
  const { user, saveUserData, getUserData, updateUserData, getAuthHeaders } = useAuth();
  const { isDemoModeActive, getDemoQueryParams, selectedProfile } = useDemo();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user data on mount
  useEffect(() => {
    if (user) {
      // Check if JWT token exists before loading data with retry logic
      const checkTokenAndLoad = (attempt = 1, maxAttempts = 5) => {
        const jwtToken = localStorage.getItem('jwtToken');
        if (jwtToken) {
          console.log(`✅ JWT token exists (attempt ${attempt}), loading user data...`);
          loadUserData();
        } else if (attempt < maxAttempts) {
          console.warn(`⚠️ User authenticated but JWT token not found yet (attempt ${attempt}/${maxAttempts}), waiting...`);
          // Retry with exponential backoff: 500ms, 1s, 2s, 4s
          const delay = Math.min(500 * Math.pow(2, attempt - 1), 4000);
          const timer = setTimeout(() => {
            checkTokenAndLoad(attempt + 1, maxAttempts);
          }, delay);
          return () => clearTimeout(timer);
        } else {
          console.error(`❌ JWT token still not found after ${maxAttempts} attempts`);
          setLoading(false);
          setError('Authentication token not available. Please try refreshing the page.');
        }
      };
      
      checkTokenAndLoad();
    } else {
      setUserData(null);
      setLoading(false);
    }
  }, [user, isDemoModeActive, selectedProfile]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use API directly for demo mode support
      if (isDemoModeActive) {
        const queryParams = getDemoQueryParams();
        const response = await fetch(buildApiUrl(`/api/user-data${queryParams}`), {
          method: 'GET',
          headers: getAuthHeaders(),
        });
        const result = await response.json();
        if (result.success) {
          const convertedData = convertFromFirestore(result.data);
          setUserData(convertedData);
          return;
        } else {
          throw new Error(result.error || 'Failed to fetch user data');
        }
      }
      
      const data = await getUserData();
      
      // Convert Firestore data to frontend format if needed
      if (data) {
        try {
          const convertedData = convertFromFirestore(data);
          setUserData(convertedData);
        } catch (conversionError) {
          console.error('Error converting Firestore data:', conversionError);
          // If conversion fails, try to use the data as-is or provide fallback
          setUserData(data);
        }
      } else {
        setUserData(null);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveData = async (data) => {
    try {
      if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');
      setError(null);
      
      // Sanitize data for Firestore storage
      const sanitizedData = sanitizeForFirestore(data);
      await saveUserData(sanitizedData);
      
      setUserData(prev => ({ ...prev, ...data }));
      return { success: true };
    } catch (err) {
      setError(err.message);
      console.error('Error saving user data:', err);
      return { success: false, error: err.message };
    }
  };

  const updateData = async (data) => {
    try {
      if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');
      setError(null);
      
      // Sanitize data for Firestore storage
      const sanitizedData = sanitizeForFirestore(data);
      await updateUserData(sanitizedData);
      
      setUserData(prev => ({ ...prev, ...data }));
      return { success: true };
    } catch (err) {
      setError(err.message);
      console.error('Error updating user data:', err);
      return { success: false, error: err.message };
    }
  };

  // Specific data management functions with validation
  const savePortfolio = async (portfolioData) => {
    // Validate each asset in the portfolio
    const validationErrors = [];
    portfolioData.forEach((asset, index) => {
      const validation = validateAsset(asset);
      if (!validation.isValid) {
        validationErrors.push(`Asset ${index + 1}: ${validation.errors.join(', ')}`);
      }
    });

    if (validationErrors.length > 0) {
      const errorMessage = `Portfolio validation failed: ${validationErrors.join('; ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    return await saveData({ portfolio: portfolioData });
  };

  const saveExpenses = async (expensesData) => {
    // Validate each expense
    const validationErrors = [];
    expensesData.forEach((expense, index) => {
      const validation = validateExpense(expense);
      if (!validation.isValid) {
        validationErrors.push(`Expense ${index + 1}: ${validation.errors.join(', ')}`);
      }
    });

    if (validationErrors.length > 0) {
      const errorMessage = `Expenses validation failed: ${validationErrors.join('; ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    return await saveData({ expenses: expensesData });
  };

  const saveIncome = async (incomeData) => {
    // Validate each income source
    const validationErrors = [];
    incomeData.forEach((income, index) => {
      const validation = validateIncome(income);
      if (!validation.isValid) {
        validationErrors.push(`Income ${index + 1}: ${validation.errors.join(', ')}`);
      }
    });

    if (validationErrors.length > 0) {
      const errorMessage = `Income validation failed: ${validationErrors.join('; ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    return await saveData({ income: incomeData });
  };

  const saveGoals = async (goalsData) => {
    return await saveData({ goals: goalsData });
  };

  const updatePortfolio = async (portfolioData) => {
    return await updateData({ portfolio: portfolioData });
  };

  const updateExpenses = async (expensesData) => {
    return await updateData({ expenses: expensesData });
  };

  const updateIncome = async (incomeData) => {
    return await updateData({ income: incomeData });
  };

  const updateGoals = async (goalsData) => {
    return await updateData({ goals: goalsData });
  };

  // New granular save functions for planning screens
  const saveSurplusRouting = async (routingItems) => {
    try {
      if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');
      setError(null);
      // Normalize routing: ensure Cash exists; if no rows or all excluded/zero, set Cash 100%
      const items = Array.isArray(routingItems) ? routingItems : [];
      const filtered = items
        .filter(r => r && typeof r.assetClass === 'string')
        .map(r => ({ assetClass: r.assetClass, include: Boolean(r.include), weight: Math.max(0, Math.min(100, Number(r.weight) || 0)) }));
      const hasAnyIncluded = filtered.some(r => r.include && r.weight > 0);
      let ensured = filtered.slice();
      const hasCash = ensured.some(r => r.assetClass === 'Cash');
      if (!hasCash) ensured.push({ assetClass: 'Cash', include: true, weight: 0 });
      if (!hasAnyIncluded) {
        ensured = ensured.map(r => r.assetClass === 'Cash' ? { ...r, include: true, weight: 100 } : { ...r, include: false, weight: 0 });
      } else {
        const sumOthers = ensured.filter(r => r.assetClass !== 'Cash' && r.include).reduce((s, r) => s + (Number(r.weight) || 0), 0);
        const remainder = Math.max(0, 100 - sumOthers);
        ensured = ensured.map(r => r.assetClass === 'Cash' ? { ...r, include: true, weight: remainder } : r);
      }

      const response = await fetch(buildApiUrl('/api/user-data/surplus-routing'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ surplusRouting: ensured })
      });
      if (!response.ok) throw new Error('Failed to save surplus routing');
      setUserData(prev => ({ ...prev, surplusRouting: ensured }));
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const saveDrawdownPriority = async (priority) => {
    try {
      if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');
      setError(null);
      const response = await fetch(buildApiUrl('/api/user-data/drawdown-priority'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ drawdownPriority: priority })
      });
      if (!response.ok) throw new Error('Failed to save drawdown priority');
      setUserData(prev => ({ ...prev, drawdownPriority: priority }));
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const saveSpecialEvents = async (events) => {
    try {
      if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');
      setError(null);
      const response = await fetch(buildApiUrl('/api/user-data/special-events'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ specialEvents: events })
      });
      if (!response.ok) throw new Error('Failed to save special events');
      setUserData(prev => ({ ...prev, specialEvents: events }));
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const saveEmergencyFund = async (config) => {
    try {
      if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');
      setError(null);
      const response = await fetch(buildApiUrl('/api/user-data/emergency-fund'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ emergencyFund: config })
      });
      if (!response.ok) throw new Error('Failed to save emergency fund');
      setUserData(prev => ({ ...prev, emergencyFund: config }));
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Add single item functions
  const addIncomeSource = async (incomeSource) => {
    const validation = validateIncome(incomeSource);
    if (!validation.isValid) {
      const errorMessage = `Income validation failed: ${validation.errors.join(', ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    const currentIncome = userData?.income || [];
    const updatedIncome = [...currentIncome, incomeSource];
    return await saveIncome(updatedIncome);
  };

  const addExpense = async (expense) => {
    const validation = validateExpense(expense);
    if (!validation.isValid) {
      const errorMessage = `Expense validation failed: ${validation.errors.join(', ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    const currentExpenses = userData?.expenses || [];
    const updatedExpenses = [...currentExpenses, expense];
    return await saveExpenses(updatedExpenses);
  };

  const addAsset = async (asset) => {
    const validation = validateAsset(asset);
    if (!validation.isValid) {
      const errorMessage = `Asset validation failed: ${validation.errors.join(', ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    const currentPortfolio = userData?.portfolio || [];
    const updatedPortfolio = [...currentPortfolio, asset];
    return await savePortfolio(updatedPortfolio);
  };

  // Update single item functions
  const updateIncomeSource = async (updatedIncome) => {
    const validation = validateIncome(updatedIncome);
    if (!validation.isValid) {
      const errorMessage = `Income validation failed: ${validation.errors.join(', ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    const currentIncome = userData?.income || [];
    const updatedIncomeList = currentIncome.map(income => 
      income.id === updatedIncome.id ? updatedIncome : income
    );
    return await saveIncome(updatedIncomeList);
  };

  const updateExpenseItem = async (updatedExpense) => {
    const validation = validateExpense(updatedExpense);
    if (!validation.isValid) {
      const errorMessage = `Expense validation failed: ${validation.errors.join(', ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    const currentExpenses = userData?.expenses || [];
    const updatedExpensesList = currentExpenses.map(expense => 
      expense.id === updatedExpense.id ? updatedExpense : expense
    );
    return await saveExpenses(updatedExpensesList);
  };

  const updateAssetItem = async (updatedAsset) => {
    const validation = validateAsset(updatedAsset);
    if (!validation.isValid) {
      const errorMessage = `Asset validation failed: ${validation.errors.join(', ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    const currentPortfolio = userData?.portfolio || [];
    const updatedPortfolioList = currentPortfolio.map(asset => 
      asset.id === updatedAsset.id ? updatedAsset : asset
    );
    return await savePortfolio(updatedPortfolioList);
  };

  // Delete single item functions
  const deleteIncomeSource = async (incomeId) => {
    const currentIncome = userData?.income || [];
    const updatedIncome = currentIncome.filter(income => income.id !== incomeId);
    return await saveIncome(updatedIncome);
  };

  const deleteExpense = async (expenseId) => {
    const currentExpenses = userData?.expenses || [];
    const updatedExpenses = currentExpenses.filter(expense => expense.id !== expenseId);
    return await saveExpenses(updatedExpenses);
  };

  const deleteAsset = async (assetId) => {
    const currentPortfolio = userData?.portfolio || [];
    const updatedPortfolio = currentPortfolio.filter(asset => asset.id !== assetId);
    return await savePortfolio(updatedPortfolio);
  };

  return {
    userData,
    loading,
    error,
    loadUserData,
    saveData,
    updateData,
    savePortfolio,
    saveExpenses,
    saveIncome,
    saveGoals,
    updatePortfolio,
    updateExpenses,
    updateIncome,
    updateGoals,
    saveSurplusRouting,
    saveDrawdownPriority,
    saveSpecialEvents,
    saveEmergencyFund,
    addIncomeSource,
    addExpense,
    addAsset,
    updateIncomeSource,
    updateExpenseItem,
    updateAssetItem,
    deleteIncomeSource,
    deleteExpense,
    deleteAsset
  };
}; 